import { IObservable, Observable } from "thrive-observable-react";

//interfaces
import { SignUpStepRoutes, SignUpSteps } from "../../interfaces/Navigation";
import { IndustryType } from "../../interfaces/Industry";
import { IUserDetailsForm } from "../../interfaces/FormData";

//utilities
import { IPasswordIncludes, validatePassword } from "../../utils/validation";

//services
import { navigateTo } from "../../services/navigation.service";
import { SUACService } from "../../services/SignUpAppConfig.service";
import { IValidateCode } from "../../interfaces/api";
import { validateCode, validateEmailAddress } from "../../api/selfSignUp";



class DetailsModel {
    public title: string = 'Tell us about you';
    public subtitle: string = 'We just need a few details to set up your demo workspace.';
    public userFormHeading: string = 'YOU';
    public workspaceFormHeading: string = "YOUR WORKSPACE";
    public button: string = "Create app & continue"
    public buttonIsDisabled: IObservable<boolean> = Observable(true);

    //FORM VALS
    public firstName: IObservable<string> = Observable("");
    public lastName: IObservable<string> = Observable("");
    public workspaceName: IObservable<string> = Observable("");
    public password: IObservable<string> = Observable("");
    public emailAddress: IObservable<string> = Observable("");
    public emailError: IObservable<string> = Observable("");
    public validationCode: IObservable<string> = Observable("");
    public termsAndConditionsAgreed: IObservable<boolean> = Observable(false);
    public passwordIsValid: IObservable<boolean> = Observable(false);
    public passwordElementsCompleted: IObservable<IPasswordIncludes[]> = Observable([]);
    public industry: IObservable<IndustryType | undefined> = Observable<IndustryType | undefined>(undefined);
    
    //validation
    public validateCases: IObservable<boolean> = Observable(false);
    public validateNumber: IObservable<boolean> = Observable(false);
    public validateSpecialChar: IObservable<boolean> = Observable(false);
    public validateLength: IObservable<boolean> = Observable(false);
    public showValidationModal : IObservable<boolean> = Observable(false);
    public showTermsAndConditionsModal : IObservable<boolean> = Observable(false);
    public errorList: IObservable<string[]> = Observable([]);
    public formHasBeenChanged : IObservable<boolean> = Observable(false);
    public codeIsInvalid: IObservable<boolean> = Observable(false);
    public countdown: IObservable<number> = Observable(0);
    private resendTimer?: ReturnType<typeof setInterval>;
    
    private addError = (field: string) => {
        if (!this.errorList.value.includes(field)) {
            this.errorList.update([...this.errorList.value, field]);
        }
    }

    private removeError = (field: string) => {
        this.errorList.update(this.errorList.value.filter(e => e !== field));
    }

    public validateField = (field: string) => {
        switch (field) {
            case "firstName":
                if (this.firstName.value.trim().length === 0) this.addError(field);
                else this.removeError(field);
                break;

            case "lastName":
                if (this.lastName.value.trim().length === 0) this.addError(field);
                else this.removeError(field);
                break;

            case "emailAddress":
                if (this.emailAddress.value.trim().length === 0) {
                    this.emailError.update("Valid email address is required")
                    this.addError(field);
                } else this.removeError(field);
                break;

            case "emailAddressValidity":
                if (this.emailAddress.value.trim().length === 0 || !this.emailAddress.value.includes("@")) {
                    this.emailError.update("Valid email address is required")
                    this.addError("emailAddress");
                } else this.removeError("emailAddress");
                break;

            case "workspaceName":
                if (this.workspaceName.value.trim().length === 0) this.addError(field);
                else this.removeError(field);
                break;

            case "password":
                if (!this.passwordIsValid.value) this.addError(field);
                else this.removeError(field);
                break;

            case "industry":
                if (!this.industry.value) this.addError(field);
                else this.removeError(field);
                break;
        }
    }

    private updateField = (observable: IObservable<string>, val: string, field: string) => {
        observable.update(val);
        this.validateField(field);
        this.checkIfButtonIsDisabled();
        this.formHasBeenChanged.update(true);
    }

    private hasValue(value: string) {
        return value !== undefined && value !== null && value.trim().length > 0;
    }

    setFirstname = (val: string) => {
        this.updateField(this.firstName, val, "firstName");
    }

    setLastname = (val: string) => {
        this.updateField(this.lastName, val, "lastName");
    }

    setEmailAddress = (val: string) => {
        this.emailError.update("");
        this.updateField(this.emailAddress, val, "emailAddress");
    } 

    setWorkspaceName = (val: string) => {
        this.updateField(this.workspaceName, val, "workspaceName");
    }

    setTermsAndConditionsAgreed = (isAgreed: boolean) => {
        this.termsAndConditionsAgreed.update(isAgreed);
        this.checkIfButtonIsDisabled();
        this.formHasBeenChanged.update(true);
    }

    setIndustry = (industry: IndustryType) => {
        this.industry.update(industry);
        this.validateField("industry");
        this.checkIfButtonIsDisabled();
        this.formHasBeenChanged.update(true);
    }

    setPassword = (val:string) => {
        this.password.update(val);
        validatePassword(val);

        const {passwordIsValid, passwordIncludes} = validatePassword(val);

        // update password validation observables
        this.passwordIsValid.update(passwordIsValid);
        this.passwordElementsCompleted.update(passwordIncludes);
        this.validateCases.update(passwordIncludes.includes('uppercaseAndLowercase'));
        this.validateNumber.update(passwordIncludes.includes('number'));
        this.validateSpecialChar.update(passwordIncludes.includes('specialChar'));
        this.validateLength.update(passwordIncludes.includes('maxLength'));
        this.checkIfButtonIsDisabled();
    }
    
    checkIfButtonIsDisabled = () => {
        const isFormComplete =
            this.hasValue(this.firstName.value) &&
            this.hasValue(this.lastName.value) &&
            this.hasValue(this.emailAddress.value) &&
            this.hasValue(this.workspaceName.value) &&
            this.hasValue(this.password.value) &&
            this.industry.value !== undefined &&
            this.termsAndConditionsAgreed.value;

        const isPasswordValid = this.passwordIsValid.value;
        this.buttonIsDisabled.update(!(isFormComplete && isPasswordValid));
    }

    public onAgreeClick = () => {
        this.setShowTermsAndConditionsModal(false);
        this.termsAndConditionsAgreed.update(true);
    }

    public setShowTermsAndConditionsModal = (show: boolean) => {
        this.showTermsAndConditionsModal.update(show);
    }

    public setValidationCode = (code: string) => {
        this.codeIsInvalid.update(false);
        this.validationCode.update(code);
    }

    public onButtonClick = async () => {
        const res = await validateEmailAddress({emailAddress : this.emailAddress.value, firstName: this.firstName.value})
        .catch(error => {
            this.emailError.update(error.response?.data?.message || "Something went wrong with validating your email");
            this.addError("emailAddress");
        });
        
        if (res?.status === 200) {
            this.showValidationModal.update(true);
        }
    }

    public onHideValidationModal = () => {
        this.showValidationModal.update(false);
        this.codeIsInvalid.update(false);
    }
    
    private startResendCountdown = () => {
        this.countdown.update(30);

        if (this.resendTimer) {
            clearInterval(this.resendTimer);
        }

        this.resendTimer = setInterval(() => {
            const current = this.countdown.value;

            if (current <= 1) {
                clearInterval(this.resendTimer);
                this.countdown.update(0);
            } else {
                this.countdown.update(current - 1);
            }
        }, 1000);
    }

    public onResendCodeClick = () => {
        this.startResendCountdown();

        this.onButtonClick();
    }

    public onValidateClick = async () => {
        const params : IValidateCode = {
            code: this.validationCode.value,
            companyName: this.workspaceName.value,
            industry: this.industry.value as IndustryType,
        }

        const res = await validateCode(params)
        .catch(() => {
            this.codeIsInvalid.update(true);
        });
        
        if(res?.data) {
            //update form
            const form: IUserDetailsForm = {
                code: this.validationCode.value,
                firstName: this.firstName.value,
                lastName: this.lastName.value,
                emailAddress: this.emailAddress.value,
                password: this.password.value,
                companyName: this.workspaceName.value,
                numberOfEmployees: 1,
                phoneNumber: "00000000000",
                industry: this.industry.value as IndustryType,
            };

            SUACService.setUpdateCode(this.validationCode.value);
            SUACService.saveUserDetailsToForm(form);        
            SUACService.updateContentTiles(res.data.content);
            SUACService.setAppName(res.data.appName);
            SUACService.setOrgShortName(res.data.orgShortName);

            //navigate to next step
            SUACService.setCurrentStep(SignUpSteps.DESIGN);
            navigateTo(SignUpStepRoutes[SignUpSteps.DESIGN]);
        }
    }
}


export { DetailsModel };